import { FC, useContext, useState } from "react";
import { faObjectGroup } from "@fortawesome/free-solid-svg-icons";
import { FindestButton, FindestTextBox } from "Components/Shared";
import { GroupTermElementTypeEnum, GroupTermFunctionTypeEnum, SynonymTypeEnum, ToastTypeEnum } from "Enums";
import { TermGroupControllerSingleton } from "Controllers";
import { CaseContext } from "Stores";
import { ToastHelperSingleton } from "Helpers";
import { SavedQueryContext } from "Providers";

type TCreateSynonymGroupProps = {
    synonymType: SynonymTypeEnum,
    parentElementId: number,
};

export const CreateSynonymGroup: FC<TCreateSynonymGroupProps> = ({ synonymType, parentElementId }: TCreateSynonymGroupProps) => {

    // Context
    const { caseState } = useContext(CaseContext);
    const { savedQuery, updateSavedQuery } = useContext(SavedQueryContext);

    // State
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const [groupName, setGroupName] = useState("");

    const onChangeGroupName = (value: string) => {
        setGroupName(value);
    };

    const createGroup = async () => {
        if(!caseState.selectedCaseId || !savedQuery) return;

        if(synonymType !== SynonymTypeEnum.Action && synonymType !== SynonymTypeEnum.Object && synonymType !== SynonymTypeEnum.Environment) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Did not recognize the element to create the group for.");
            return;
        }

        if(groupName.trim().length === 0) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please enter a group name");
            return;
        }

        const elementType = synonymType === SynonymTypeEnum.Action || synonymType === SynonymTypeEnum.Object ?
            GroupTermElementTypeEnum.FunctionTerm : GroupTermElementTypeEnum.Other;
        const functionType = synonymType === SynonymTypeEnum.Action ? 
            GroupTermFunctionTypeEnum.Action : GroupTermFunctionTypeEnum.Other;

        const isSuccess = await TermGroupControllerSingleton.createAsync(caseState.selectedCaseId, groupName, parentElementId,
            elementType, functionType);

        if(!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Failed to create search term group.");
            return;
        }

        if(synonymType === SynonymTypeEnum.Action || synonymType === SynonymTypeEnum.Object) {
            for (const actionObject of savedQuery.actionObjects) {
                if (actionObject.id === parentElementId) {
                    if (synonymType === SynonymTypeEnum.Action) {
                        actionObject.actionTermGroupName = groupName;
                    } else {
                        actionObject.objectTermGroupName = groupName;
                    }
                }
            }

            savedQuery.actionObjects = [...savedQuery.actionObjects];
            updateSavedQuery(savedQuery);
        } else if(synonymType === SynonymTypeEnum.Environment) {
            for (const environment of savedQuery.environmentVariables) {
                if (environment.id === parentElementId) {
                    environment.termGroupName = groupName;
                }
            }

            savedQuery.environmentVariables = [...savedQuery.environmentVariables];
            updateSavedQuery(savedQuery);
        }

        setIsCreatingGroup(false);
    };

    return (
        <div>
            <FindestButton title="Create group" iconName={faObjectGroup} onClick={ () => { setIsCreatingGroup(!isCreatingGroup); }} />
            {isCreatingGroup ?
                <div>
                    <FindestTextBox placeholder="Group name" onChange={onChangeGroupName} onEnter={createGroup} />
                    <FindestButton title="Create" onClick={createGroup} />
                </div>
                : null
            }
        </div>
    );
};